import DashboardService from "@/src/api/DashboardService.js";
import { entityTypes } from "@/src/constants/entityTypeConstants";

const state = {
  pipelines: [],
  pipelineStages: {},
  lastFetchTime: null,
  isLoading: false,
  showWeightedStats: false,
};

const getters = {
  allPipelines: (state) => state.pipelines,

  pipelineById: (state) => (id) => {
    return state.pipelines.find((p) => p.id === id);
  },

  stagesByPipelineId: (state) => (pipelineId) => {
    return state.pipelineStages[pipelineId]?.stages || [];
  },

  allStages: (state) => {
    const allStages = [];
    Object.values(state.pipelineStages).forEach((data) => {
      if (data.stages) {
        allStages.push(...data.stages);
      }
    });
    return allStages;
  },

  isPipelinesLoaded: (state) => {
    return state.pipelines.length > 0;
  },

  isStagesLoadedForPipeline: (state) => (pipelineId) => {
    return !!state.pipelineStages[pipelineId];
  },

  isLoading: (state) => state.isLoading,
};

const mutations = {
  SET_PIPELINES(state, pipelines) {
    state.pipelines = pipelines;
    state.lastFetchTime = Date.now();
  },

  SET_SHOW_WEIGHTED_STATS(state, value) {
    state.showWeightedStats = value;
  },

  SET_PIPELINE_STAGES(state, { pipelineId, stages }) {
    state.pipelineStages = {
      ...state.pipelineStages,
      [pipelineId]: {
        stages,
        timestamp: Date.now(),
      },
    };
  },

  SET_ALL_STAGES(state, stages) {
    // Store all stages under empty string key for "all pipelines" view
    state.pipelineStages = {
      ...state.pipelineStages,
      "": {
        stages,
        timestamp: Date.now(),
      },
    };
  },

  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },

  CLEAR_CACHE(state) {
    state.pipelines = [];
    state.pipelineStages = {};
    state.lastFetchTime = null;
    state.showWeightedStats = false;
  },

  ADD_PIPELINE(state, pipeline) {
    state.pipelines.push(pipeline);
  },

  UPDATE_PIPELINE(state, updatedPipeline) {
    const index = state.pipelines.findIndex((p) => p.id === updatedPipeline.id);
    if (index !== -1) {
      state.pipelines[index] = updatedPipeline;
    }
  },

  REMOVE_PIPELINE(state, pipelineId) {
    state.pipelines = state.pipelines.filter((p) => p.id !== pipelineId);

    if (state.pipelineStages[pipelineId]) {
      delete state.pipelineStages[pipelineId];
    }
  },

  CLEAR_PIPELINE_STAGES(state, pipelineId) {
    if (state.pipelineStages[pipelineId]) {
      delete state.pipelineStages[pipelineId];
    }

    if (state.pipelineStages[""]) {
      delete state.pipelineStages[""];
    }
  },
};

const actions = {
  async fetchPipelines({ commit, state }, { force = false } = {}) {
    if (!force && state.pipelines.length > 0) {
      return { success: true, pipelines: state.pipelines, show_weighted_stats: state.showWeightedStats };
    }

    try {
      commit("SET_LOADING", true);
      const response = await DashboardService.fetchPipelines();

      if (response.success && response.pipelines) {
        commit("SET_PIPELINES", response.pipelines);
        commit("SET_SHOW_WEIGHTED_STATS", response.show_weighted_stats || false);
        return { success: true, pipelines: response.pipelines, show_weighted_stats: response.show_weighted_stats || false };
      }

      return { success: false, error: "Failed to fetch pipelines" };
    } catch (error) {
      console.error("Error fetching pipelines:", error);
      return { success: false, error: error.message };
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchPipelineStages(
    { commit, state },
    { pipelineId = "", pipelineIds = [], force = false } = {},
  ) {
    // Handle batch fetch for multiple pipeline IDs
    if (Array.isArray(pipelineIds) && pipelineIds.length > 0) {
      // Check cache for all requested IDs
      if (!force) {
        const allCached = pipelineIds.every((id) => state.pipelineStages[id]);
        if (allCached) {
          const cachedStages = pipelineIds.flatMap(
            (id) => state.pipelineStages[id]?.stages || [],
          );
          // When multiple pipelines are selected, add pipeline name prefix for disambiguation
          const formattedCachedStages =
            pipelineIds.length > 1
              ? cachedStages.map((stage) => ({
                  ...stage,
                  name:
                    stage.pipeline_name &&
                    !stage.name.includes(`(${stage.pipeline_name})`)
                      ? `${stage.name} (${stage.pipeline_name})`
                      : stage.name,
                }))
              : cachedStages;
          return { success: true, pipeline_stages: formattedCachedStages };
        }
      }

      try {
        commit("SET_LOADING", true);
        const response =
          await DashboardService.getPipelineStagesBatch(pipelineIds);

        if (response.success && response.pipeline_stages) {
          // When multiple pipelines are selected, add pipeline name prefix for disambiguation
          const stages =
            pipelineIds.length > 1
              ? response.pipeline_stages.map((stage) => ({
                  ...stage,
                  name: stage.pipeline_name
                    ? `${stage.name} (${stage.pipeline_name})`
                    : stage.name,
                }))
              : response.pipeline_stages;

          // Group stages by pipeline and cache them individually (without prefix for reuse)
          const stagesByPipeline = {};
          response.pipeline_stages.forEach((stage) => {
            const pid = stage.pipeline_id;
            if (pid) {
              if (!stagesByPipeline[pid]) {
                stagesByPipeline[pid] = [];
              }
              stagesByPipeline[pid].push(stage);
            }
          });

          Object.entries(stagesByPipeline).forEach(([pid, pipelineStages]) => {
            commit("SET_PIPELINE_STAGES", {
              pipelineId: pid,
              stages: pipelineStages,
            });
          });

          return { success: true, pipeline_stages: stages };
        }

        return { success: false, error: "Failed to fetch pipeline stages" };
      } catch (error) {
        console.error("Error fetching pipeline stages:", error);
        return { success: false, error: error.message };
      } finally {
        commit("SET_LOADING", false);
      }
    }

    // Original single pipeline ID logic
    if (!force && state.pipelineStages[pipelineId]) {
      return {
        success: true,
        pipeline_stages: state.pipelineStages[pipelineId].stages,
      };
    }

    try {
      commit("SET_LOADING", true);
      const response = await DashboardService.getPipelineStages(pipelineId);

      if (response.success && response.pipeline_stages) {
        const stages = response.pipeline_stages.map((stage) => {
          if (!pipelineId && stage.pipeline_name) {
            return {
              ...stage,
              name: `${stage.name} (${stage.pipeline_name})`,
            };
          }
          return stage;
        });

        if (pipelineId) {
          commit("SET_PIPELINE_STAGES", { pipelineId, stages });
        } else {
          commit("SET_ALL_STAGES", stages);
        }

        return { success: true, pipeline_stages: stages };
      }

      return { success: false, error: "Failed to fetch pipeline stages" };
    } catch (error) {
      console.error("Error fetching pipeline stages:", error);
      return { success: false, error: error.message };
    } finally {
      commit("SET_LOADING", false);
    }
  },

  clearCache({ commit }) {
    commit("CLEAR_CACHE");
  },

  async refreshPipelines({ dispatch }) {
    return dispatch("fetchPipelines", { force: true });
  },

  async refreshPipelineStages({ dispatch }, { pipelineId = "" } = {}) {
    return dispatch("fetchPipelineStages", { pipelineId, force: true });
  },

  async pipelineCreated({ dispatch }) {
    await dispatch("refreshPipelines");
    await dispatch(
      "schema/refreshSchema",
      { slug: entityTypes.DEALS },
      { root: true },
    );
  },

  async pipelineUpdated({ dispatch, commit }, { pipelineId } = {}) {
    await dispatch("refreshPipelines");
    if (pipelineId) {
      commit("CLEAR_PIPELINE_STAGES", pipelineId);
    }
    await dispatch(
      "schema/refreshSchema",
      { slug: entityTypes.DEALS },
      { root: true },
    );
  },

  async pipelineDeleted({ commit, dispatch }, { pipelineId }) {
    commit("REMOVE_PIPELINE", pipelineId);
    await dispatch(
      "schema/refreshSchema",
      { slug: entityTypes.DEALS },
      { root: true },
    );
  },

  async stagesUpdated({ commit }, { pipelineId }) {
    commit("CLEAR_PIPELINE_STAGES", pipelineId);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
